import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";
import { useTranslation } from "react-i18next";
import {
  cancelPasswordCreationRequestAsync,
  relaunchPasswordCreationRequestAsync,
  selectManagersList,
} from "./usersSlice";
import { PublicManager } from "../homeSlice";
import "../../../i18n/i18n";
import { Option } from "@qivia/ui/src/designSystem/components/Menu/MenuOptions";
import { useCallback, useMemo } from "react";
import { ManagerDataDisplayRow } from "./usersAPI";
import {
  CancelOutlined,
  LoopOutlined,
  NotInterestedOutlined,
  SendOutlined,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useAppDispatch } from "../../../redux/hooks";

export const useMenuOptionsUsers = (props: {
  currentManager: PublicManager | null;
  setManagerToDeactivate: (
    managerToDeactivate: ManagerDataDisplayRow | null,
  ) => void;
}): Option[] => {
  const { currentManager, setManagerToDeactivate } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const managersList = useAppSelector(selectManagersList);

  const getManagerToHandle = useCallback(
    (props: { managerToHandleUuid?: string }) => {
      if (!props.managerToHandleUuid) {
        return null;
      }
      return managersList.find((m) => m.uuid === props.managerToHandleUuid);
    },
    [managersList],
  );

  const isOwnerManager: boolean = useMemo(() => {
    return currentManager?.role === "OWNER";
  }, [currentManager]);

  const canDeactivateManager = useCallback(
    (props: { managerToHandleUuid?: string }) => {
      const managerToHandle = getManagerToHandle(props);
      if (!managerToHandle) {
        return false;
      }
      const isDisabled =
        currentManager?.role === "OWNER" &&
        managerToHandle.status === "ACTIVE" &&
        managerToHandle.role !== "OWNER" &&
        props.managerToHandleUuid !== currentManager?.uuid;

      return isDisabled;
    },
    [currentManager?.role, currentManager?.uuid, getManagerToHandle],
  );

  const canUpdateRoleManager = useCallback(
    (props: { managerToHandleUuid?: string }) => {
      const managerToHandle = getManagerToHandle(props);
      if (!managerToHandle) {
        return false;
      }
      const isDisabled =
        currentManager?.role === "OWNER" &&
        managerToHandle.role !== "OWNER" &&
        props.managerToHandleUuid !== currentManager?.uuid;

      return isDisabled;
    },
    [currentManager?.role, currentManager?.uuid, getManagerToHandle],
  );

  const canRelaunchPasswordRequestManager = useCallback(
    (props: { managerToHandleUuid?: string }) => {
      const managerToHandle = getManagerToHandle(props);
      if (!managerToHandle) {
        return false;
      }

      const isPendingManagerToHandle =
        managerToHandle.status &&
        managerToHandle.status !== "ACTIVE" &&
        managerToHandle.status !== "DEACTIVATED";
      const isDisabled = isOwnerManager && isPendingManagerToHandle;
      return isDisabled;
    },
    [getManagerToHandle, isOwnerManager],
  );

  const canCancelPasswordRequestManager = useCallback(
    (props: { managerToHandleUuid?: string }) => {
      const managerToHandle = getManagerToHandle(props);
      if (!managerToHandle) {
        return false;
      }
      const isPendingManagerToHandle =
        managerToHandle.status &&
        managerToHandle.status !== "ACTIVE" &&
        managerToHandle.status !== "DEACTIVATED";

      const canLastCreatedPasswordRequestBeCanceled =
        managerToHandle.status &&
        managerToHandle.status !== "CANCELED" &&
        managerToHandle.status !== "EXPIRED";

      const isPendingRequest = isOwnerManager && isPendingManagerToHandle;
      const isDisabled =
        isPendingRequest && canLastCreatedPasswordRequestBeCanceled;
      return isDisabled;
    },
    [getManagerToHandle, isOwnerManager],
  );

  const handleManagerToDeactivate = useCallback(
    (props: { managerToHandleUuid?: string }) => {
      const managerToHandle = getManagerToHandle(props);
      setManagerToDeactivate(managerToHandle as ManagerDataDisplayRow);
    },
    [getManagerToHandle, setManagerToDeactivate],
  );

  const StyledLabel = styled.div`
    display: flex;
    align-items: center;
  `;

  return useMemo<Option[]>(
    () => [
      {
        label: (
          <StyledLabel>
            <LoopOutlined />
            <Spacer x={0.5} />
            <TextCapitalized>
              {t("users.management.sidePanel.updateRole")}
            </TextCapitalized>
          </StyledLabel>
        ),
        action: (id) =>
          id &&
          canUpdateRoleManager({ managerToHandleUuid: id }) &&
          navigate(`/home/user_update_role/${id}`),
        isDisabled: (id) => {
          return !canUpdateRoleManager({ managerToHandleUuid: id });
        },
      },

      {
        label: (
          <StyledLabel>
            <SendOutlined />
            <Spacer x={0.5} />
            <TextCapitalized>
              {t("users.management.sidePanel.relaunch")}
            </TextCapitalized>
          </StyledLabel>
        ),
        action: (id) =>
          id &&
          canRelaunchPasswordRequestManager({ managerToHandleUuid: id }) &&
          void dispatch(
            relaunchPasswordCreationRequestAsync({
              managerUuid: id,
            }),
          ),
        isDisabled: (id) => {
          return !canRelaunchPasswordRequestManager({
            managerToHandleUuid: id,
          });
        },
      },
      {
        label: (
          <StyledLabel>
            <CancelOutlined />
            <Spacer x={0.5} />
            <TextCapitalized>
              {t("users.management.sidePanel.cancel")}
            </TextCapitalized>
          </StyledLabel>
        ),
        action: (id) =>
          id &&
          canCancelPasswordRequestManager({ managerToHandleUuid: id }) &&
          void dispatch(
            cancelPasswordCreationRequestAsync({
              managerUuid: id,
            }),
          ),
        isDisabled: (id) => {
          return !canCancelPasswordRequestManager({
            managerToHandleUuid: id,
          });
        },
      },
      {
        label: (
          <StyledLabel>
            <NotInterestedOutlined />
            <Spacer x={0.5} />
            <TextCapitalized>
              {t("users.management.sidePanel.deactivate")}
            </TextCapitalized>
          </StyledLabel>
        ),
        color: colors["colors/system/error/error_normal"],
        action: (id) =>
          id &&
          canDeactivateManager({ managerToHandleUuid: id }) &&
          handleManagerToDeactivate({ managerToHandleUuid: id }),
        isDisabled: (id) => {
          return !canDeactivateManager({ managerToHandleUuid: id });
        },
      },
    ],
    [
      StyledLabel,
      t,
      canUpdateRoleManager,
      navigate,
      canRelaunchPasswordRequestManager,
      dispatch,
      canCancelPasswordRequestManager,
      canDeactivateManager,
      handleManagerToDeactivate,
    ],
  );
};
